import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  AppButton,
  Box,
  ButtonResponsiveWrapper,
  ErrorModal,
  FiservCardHolder,
  FlexControlledForm,
  Hint,
  Inscription,
  Preloader,
  Text,
  useResponsive,
} from '@common-fe/common-fe';
import { Option } from '@common-fe/common-fe';
import * as yup from 'yup';

import regexp from '@/common/regexp';
import { PendingModal } from '@/components';
import { LimitedAccessLabel, SuccessModal } from '@/components/elements';
import { ModalWrapper } from '@/components/wrappers';
import { useSnackbar } from '@/modules/core/hooks';
import useEmployerTerminationBanner from '@/modules/core/hooks/useEmployerTerminationBanner';
import {
  AddCardDependentForm,
  DependentFormValue,
} from '@/modules/transaction/components/Cards/components/AddCardDependentModal/AddCardDependentForm';
import { useGetRelationshipTypesQuery } from '@/modules/transaction/hooks/useGetRelationshipTypes.query';
import { useGetCardsQuery } from '@/modules/transaction/queries/useGetCards.query';
import { useAuthStore } from '@/modules/user/stores';
import { capitalizeFirstLetter } from '@/utils';
import useSsnConflictError from '@/utils/hooks/useSsnConflictError';

import { CardDependentData, useCardDependentForm } from './hooks/useCardDependentForm';
import { useFilteredDependents } from './hooks/useFilteredDependents';
import { useFutureDatedEmployee } from './hooks/useFutureDatedEmployee';
import { useSetDependent } from './hooks/useSetDependent';
import { useTypeForm } from './hooks/useTypeForm';
import {
  useAddCardDependentMutation,
  useUpdateCardDependentMutation,
} from './queries/useAddDependentCard.query';
import { StyledContent } from './AddCardDependentModal.styles';

const HINT_TEXT = 'Terminated Organization cannot add dependents';

interface DiscardConfirmationState {
  visible: boolean;
  type: string | null;
}

interface Props {
  isLimitedAccess?: boolean;
}

const CREATE_DEPENDENT_TYPE = 'new';

export const AddCardDependentModal: React.FC<Props> = ({ isLimitedAccess }) => {
  const { t } = useTranslation();
  const { isMobile } = useResponsive();
  const { user } = useAuthStore();
  const { isFutureDatedEmployee, futureEmploymentHint } = useFutureDatedEmployee({
    employeeId: user?.employee?.id,
  });
  const [dependentModalVisible, setDependentModalVisible] = useState(false);
  const [successModalVisible, setSuccessModalVisible] = useState(false);
  const [newDependent, setNewDependent] = useState<DependentFormValue | null>(null);
  const [oldDependent, setOldDependent] = useState<FiservCardHolder | null>(null);
  const [newDependentTempData, setNewDependentTempData] = useState<CardDependentData | null>(null);
  const [discardCreationConfirmation, setDiscardCreationConfirmation] = useState<DiscardConfirmationState>({
    visible: false,
    type: '',
  });
  const [showTypeError, setShowTypeError] = useState(false);
  const [showErrors, setShowErrors] = useState(false);
  const [isSsnConflictVisible, setSsnConflictVisible] = useState(false);
  const handleSsnConflictError = useSsnConflictError({ setSsnConflictVisible });
  const formRef = useRef<HTMLFormElement>(null);
  const { handleAddPermanentSnackbar } = useSnackbar();
  const { mutateAsync } = useAddCardDependentMutation();
  const { mutateAsync: updateDependentCard } = useUpdateCardDependentMutation();
  const [typeForm, setTypeForm] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const createMode = useMemo(() => typeForm === CREATE_DEPENDENT_TYPE, [typeForm]);
  const { types, isLoading: relationshipListLoading } = useGetRelationshipTypesQuery();
  const { filteredDependents, isLoading: dependentsLoading } = useFilteredDependents({
    employeeId: user?.employee?.id,
  });
  const { isDisabledDueToTermination } = useEmployerTerminationBanner();
  const {
    // holderList,
    refetch: getCardHolderByIdRefetch,
    isLoading: getCardHolderByIdLoading,
    isFetching: getCardHolderByIdFetching,
  } = useGetCardsQuery(user?.employee?.id);

  const preparedTypes = useMemo(
    () =>
      types.reduce(
        (list, currentItem) => [
          ...list,
          {
            key: currentItem.name,
            value: capitalizeFirstLetter(currentItem.name || '').replace(regexp.DASH_SYMBOL, ' '),
          },
        ],
        [] as Option[]
      ),
    [types]
  );

  // temporary solution
  const holdersWithoutRoot = useMemo(
    () => filteredDependents.filter((holder) => holder.dependentId !== 'none'),
    [filteredDependents]
  );

  const holder = useMemo(
    () => holdersWithoutRoot.find((item) => item.dependentId === typeForm),
    [holdersWithoutRoot, typeForm]
  );

  const fields = useCardDependentForm({
    createMode,
    data: createMode ? newDependentTempData : holder,
    relationshipTypes: preparedTypes,
  });

  useEffect(
    () => () => {
      setTypeForm(null);
    },
    [setTypeForm, dependentModalVisible]
  );

  useEffect(() => {
    setIsLoading(
      getCardHolderByIdLoading || getCardHolderByIdFetching || relationshipListLoading || dependentsLoading
    );
  }, [getCardHolderByIdLoading, getCardHolderByIdFetching, relationshipListLoading, dependentsLoading]);

  useEffect(() => {
    if (typeForm !== CREATE_DEPENDENT_TYPE) {
      setIsLoading(true);
      setTimeout(() => {
        setIsLoading(false);
      }, 500);
    } else {
      setIsLoading(true);
      setTimeout(() => {
        setIsLoading(false);
      }, 0);
    }
  }, [typeForm]);

  const typeFormFields = useTypeForm({
    holders: holdersWithoutRoot,
    defaultType: typeForm,
  });

  const isNewDependentValuesExist = useMemo(
    () => !!newDependent && Object.values(newDependent).some((item) => !!item),
    [newDependent]
  );

  const preparedDependent = useSetDependent(oldDependent || newDependent);

  const newDependentFullName = useMemo(
    () => `${newDependent?.firstName || ''} ${newDependent?.lastName || ''}`,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [typeForm, newDependent]
  );

  const submitButtonText = useMemo(
    () => (typeForm !== CREATE_DEPENDENT_TYPE ? t('Order a Card') : t('Add Dependent & Order a Card')),
    [typeForm, t]
  );

  const closeModal = useCallback(() => {
    setTypeForm(null);
    setDependentModalVisible(false);
    setShowTypeError(false);
  }, []);

  const closePendingModal = useCallback(() => {
    setTypeForm(CREATE_DEPENDENT_TYPE);
    setNewDependentTempData({
      firstName: newDependent?.firstName,
      lastName: newDependent?.lastName,
      relationship: newDependent?.relationship,
      dateOfBirth: newDependent?.dateOfBirth,
      ssn: newDependent?.ssn,
    } as CardDependentData);
    setDiscardCreationConfirmation((prevState) => ({
      ...prevState,
      visible: false,
      type: '',
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [typeForm]);

  useEffect(() => {
    if (typeForm) {
      const holder = holdersWithoutRoot?.find((item) => item.dependentId === typeForm);
      if (holder && !createMode) setOldDependent(holder);

      setShowTypeError(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [typeForm, createMode, setOldDependent]);

  const addCardDependentSubmit = useCallback(async () => {
    try {
      if (!typeForm) {
        setShowTypeError(true);
        return;
      }

      if (!createMode) {
        await updateDependentCard(preparedDependent);
        closeModal();
        setSuccessModalVisible(true);
        return;
      }

      setShowErrors(false);
      const validatorMap = fields.reduce(
        (map, field) => ({
          ...map,
          [field.name]: field.validator,
        }),
        {}
      );
      const schema = yup.object().shape(validatorMap);
      await schema.validate(newDependent);

      if (newDependent) {
        await mutateAsync(preparedDependent);
        closeModal();
        setSuccessModalVisible(true);
      }
    } catch (e) {
      handleSsnConflictError(e);
      setShowErrors(true);
    }
  }, [
    closeModal,
    createMode,
    fields,
    mutateAsync,
    newDependent,
    preparedDependent,
    typeForm,
    updateDependentCard,
    handleSsnConflictError,
  ]);

  return (
    <>
      {isFutureDatedEmployee ? (
        <Hint
          hintAlign="vertical"
          hintWidth={254}
          hintElement={
            <Box>
              <AppButton type="submit" width="140px" testId="add-card" disabled>
                {t('Add a Card for Dependent')}
              </AppButton>
            </Box>
          }
        >
          <Inscription size="12px" textAlign="center">
            {t(futureEmploymentHint)}
          </Inscription>
        </Hint>
      ) : (
        <Hint
          hideHint={!isLimitedAccess}
          hintAlign="left"
          hintWidth={240}
          disabled={isLimitedAccess}
          hintElement={
            <Box>
              <AppButton
                type="submit"
                width="140px"
                testId="add-card"
                disabled={isLimitedAccess}
                onClick={() => setDependentModalVisible(true)}
              >
                {t('Add a Card for Dependent')}
              </AppButton>
            </Box>
          }
        >
          <LimitedAccessLabel />
        </Hint>
      )}
      <ModalWrapper
        {...(discardCreationConfirmation.visible
          ? {
              style: { display: 'none' },
            }
          : {})}
        visible={dependentModalVisible}
        title="Add a Card for Dependent"
        onSetVisible={closeModal}
        isClosingByClickOutsideBlocked={isSsnConflictVisible}
      >
        <Box
          background="module"
          pad="medium"
          data-testid="AddCardDependentModal-form-wrapper"
          round="container1Round"
        >
          <StyledContent
            border={{ size: 'small', color: 'border2' }}
            background="canvas"
            round="container1Round"
          >
            {!discardCreationConfirmation.visible && (
              <FlexControlledForm
                fields={typeFormFields}
                editMode
                isModalType
                showError={showTypeError}
                onChangeValues={({ type }) => {
                  if (
                    typeForm &&
                    type !== typeForm &&
                    type !== CREATE_DEPENDENT_TYPE &&
                    createMode &&
                    isNewDependentValuesExist
                  ) {
                    setDiscardCreationConfirmation((prevState) => ({
                      ...prevState,
                      visible: true,
                      type,
                    }));
                  }
                  if (type && type !== typeForm) {
                    setTypeForm(type);
                  }
                }}
              />
            )}
          </StyledContent>

          <AddCardDependentForm
            onChangeValues={setNewDependent}
            type={discardCreationConfirmation.visible ? CREATE_DEPENDENT_TYPE : typeForm}
            ref={formRef}
            loading={isLoading}
            fields={fields}
            showErrors={showErrors}
          />
        </Box>

        <ButtonResponsiveWrapper justify="end" margin={{ top: 'spacing24' }}>
          <AppButton fill={isMobile} onClick={closeModal} width="160px" buttonType="secondary">
            <Text>{t('Cancel')}</Text>
          </AppButton>

          <Hint
            hintAlign="left"
            hintWidth={isMobile ? undefined : 160}
            hideHint={!isDisabledDueToTermination}
            hintElement={
              <AppButton
                fill={isMobile}
                testId="add-card_button"
                disabled={isLoading || isDisabledDueToTermination}
                onClick={addCardDependentSubmit}
                width="160px"
              >
                {isLoading ? <Preloader color="white" /> : submitButtonText}
              </AppButton>
            }
            marginContent="m"
          >
            <Box direction="row" justify="center" align="center">
              <Inscription textAlign="center">{HINT_TEXT}</Inscription>
            </Box>
          </Hint>
        </ButtonResponsiveWrapper>
      </ModalWrapper>
      <ErrorModal
        header={t('SSN must be unique')}
        buttonText={t('Got It!')}
        visible={isSsnConflictVisible}
        onSetVisible={setSsnConflictVisible}
      />
      <SuccessModal
        visible={successModalVisible}
        onSetVisible={setSuccessModalVisible}
        header="We’ve received your request for a new card!"
        helptext="Card will be mailed in 3-5 days."
        buttonText="Got It!"
        onSubmit={() => {
          handleAddPermanentSnackbar({
            text: `${preparedDependent?.firstName} ${preparedDependent?.lastName}’s card has been requested and will be mailed in 3-5 days.`,
            closeIcon: true,
          });
          setNewDependent(null);
          setOldDependent(null);
          getCardHolderByIdRefetch();
        }}
      />
      <PendingModal
        header={`Are you sure you want to discard adding ${newDependentFullName}?`}
        helptext="All inserted data will be deleted."
        buttonText="Confirm"
        visible={discardCreationConfirmation.visible}
        onCancel={closePendingModal}
        onSubmit={() => {
          setTypeForm(discardCreationConfirmation.type);
          setNewDependentTempData(null);
          setDiscardCreationConfirmation({
            visible: false,
            type: '',
          });
        }}
      />
    </>
  );
};
